// covered-by: frontends/carrie/src/routes/ask-puntt/utils/__tests__/projectContextDocuments.test.ts
import { type AskPunttProjectFile } from "@mg/schemas/src/christo/askPunttProject";

import { type ProjectReviewItem } from "../services/askPunttProjectFeed.types";

const OFFICE_EXT = /\.(docx?|xlsx?|pptx?|ppsx?|odt|ods|odp|odg|odf)$/i;

/** Derive a human-readable filename from an https/s3 source URL or storage key. */
export function referenceRowDisplayName(
  file: AskPunttProjectFile,
  version: AskPunttProjectFile["versions"][number],
): string {
  const trimmed = file.name?.trim();
  if (trimmed && trimmed !== "File") return trimmed;
  return (
    displayFileNameFromSource(version.name) ??
    displayFileNameFromSource(version.source) ??
    displayFileNameFromSource(version.key) ??
    trimmed ??
    "File"
  );
}

export function referenceDocumentTypeLabel(
  type: string,
  name: string,
  labels: { image: string; video: string; pdf: string },
): string {
  const ext = name.split(".").pop()?.toUpperCase();
  if (ext && ext.length <= 5 && ext !== name.toUpperCase()) return ext;
  if (type === "image") return labels.image;
  if (type === "video") return labels.video;
  return labels.pdf;
}

export function displayFileNameFromSource(
  sourceOrKey?: string | null,
): string | undefined {
  if (!sourceOrKey?.trim()) return undefined;
  const trimmed = sourceOrKey.trim();
  try {
    const segment = new URL(trimmed).pathname.split("/").filter(Boolean).pop();
    if (segment) return decodeURIComponent(segment);
  } catch {
    // s3://… and bare keys are not valid URLs for the URL constructor.
  }
  const path = trimmed.split("?")[0].split("#")[0];
  const segment = path.split("/").filter(Boolean).pop();
  if (!segment) return undefined;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Stable id for the same logical upload across office originals and samba's
 * converted PDF (mirrors `get_name_id` in samba: basename stem, minus "-context").
 */
export function referenceDocumentIdentity(sourceOrKey: string): string {
  const name = displayFileNameFromSource(sourceOrKey) ?? sourceOrKey.trim();
  const stem = name.split(/[?#]/)[0].replace(/\.[^.]+$/, "");
  return stem.replace(/-context$/i, "").toLowerCase();
}

function isPdfSource(sourceOrKey: string): boolean {
  const name = displayFileNameFromSource(sourceOrKey) ?? sourceOrKey;
  return /\.pdf$/i.test(name.split(/[?#]/)[0]);
}

function isOfficeSource(sourceOrKey: string): boolean {
  const name = displayFileNameFromSource(sourceOrKey) ?? sourceOrKey;
  return OFFICE_EXT.test(name.split(/[?#]/)[0]);
}

export function projectFileLatestVersion(
  file: AskPunttProjectFile,
): AskPunttProjectFile["versions"][number] | undefined {
  const versions = file.versions ?? [];
  return (
    versions.find((v) => v.versionNumber === file.currentVersion) ??
    versions[versions.length - 1]
  );
}

/** Display version from index in `file.versions` (1-based), not stored `versionNumber`. */
export function projectFileVersionOrdinal(
  file: AskPunttProjectFile,
  version: AskPunttProjectFile["versions"][number],
): number {
  const versions = file.versions ?? [];
  const idx = versions.findIndex((v) => v.versionId === version.versionId);
  return idx >= 0 ? idx + 1 : 1;
}

/**
 * Whether a file version belongs in the Reviews sidebar. Orphan
 * `in_review` stubs (auto-review bookkeeping without a Catalyst ticket) stay
 * in Reference Documents — keep in sync with christo `listReviews`.
 */
export function isProjectFileVersionListedInReviews(
  version: AskPunttProjectFile["versions"][number] | undefined,
): boolean {
  if (!version) return false;
  if (version.reviewTicketId) return true;
  return version.reviewStatus === "completed";
}

/** Mirrors the Reviews panel gate in christo `listReviews` (inverse filter). */
export function isProjectFileUnderReview(file: AskPunttProjectFile): boolean {
  return isProjectFileVersionListedInReviews(projectFileLatestVersion(file));
}

/**
 * Newest project-file version that belongs in Reference Documents (not listed in
 * Reviews). When v2 is uploaded but only v1 is in Reviews, v2 is shown here.
 */
export function projectReferenceVersion(
  file: AskPunttProjectFile,
): AskPunttProjectFile["versions"][number] | undefined {
  const versions = file.versions ?? [];
  for (let i = versions.length - 1; i >= 0; i--) {
    const v = versions[i];
    if (!isProjectFileVersionListedInReviews(v)) return v;
  }
  return undefined;
}

function sourcePathKey(sourceOrKey: string): string {
  const trimmed = sourceOrKey.trim();
  try {
    return new URL(trimmed).pathname;
  } catch {
    return trimmed.split("?")[0].split("#")[0];
  }
}

function sourcesMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a?.trim() || !b?.trim()) return false;
  const left = a.trim();
  const right = b.trim();
  if (left === right) return true;
  const leftKey = sourcePathKey(left);
  const rightKey = sourcePathKey(right);
  return (
    leftKey === rightKey ||
    leftKey.endsWith(rightKey) ||
    rightKey.endsWith(leftKey)
  );
}

function matchVersionByReviewFiles(
  files: AskPunttProjectFile[],
  reviewFiles: ProjectReviewItem["reviewFiles"] | undefined,
):
  | {
      file: AskPunttProjectFile;
      version: AskPunttProjectFile["versions"][number];
    }
  | undefined {
  for (const rf of reviewFiles ?? []) {
    const src = rf?.source?.trim();
    if (!src) continue;
    for (const file of files) {
      for (const version of file.versions ?? []) {
        if (
          sourcesMatch(version.source, src) ||
          sourcesMatch(version.key, src)
        ) {
          return { file, version };
        }
      }
    }
  }
  return undefined;
}

/** `in_review` on the file doc before `reviewTicketId` is backfilled from Catalyst. */
function matchOrphanInReviewVersion(
  files: AskPunttProjectFile[],
  item: Pick<ProjectReviewItem, "name">,
):
  | {
      file: AskPunttProjectFile;
      version: AskPunttProjectFile["versions"][number];
    }
  | undefined {
  const orphans: {
    file: AskPunttProjectFile;
    version: AskPunttProjectFile["versions"][number];
  }[] = [];
  for (const file of files) {
    for (const version of file.versions ?? []) {
      if (version.reviewStatus === "in_review" && !version.reviewTicketId) {
        orphans.push({ file, version });
      }
    }
  }
  if (orphans.length === 0) return undefined;
  if (orphans.length === 1) return orphans[0];
  const label = item.name?.trim().toLowerCase();
  if (!label) return undefined;
  const byName = orphans.filter(({ file }) => {
    const fileName = file.name?.trim().toLowerCase() ?? "";
    return (
      fileName.length > 0 &&
      (label === fileName ||
        label.includes(fileName) ||
        fileName.includes(label))
    );
  });
  return byName.length === 1 ? byName[0] : undefined;
}

function findProjectFileVersionForReview(
  files: AskPunttProjectFile[] | undefined,
  item: Pick<
    ProjectReviewItem,
    "ticketId" | "fileId" | "versionNumber" | "name" | "reviewFiles"
  >,
):
  | {
      file: AskPunttProjectFile;
      version: AskPunttProjectFile["versions"][number];
    }
  | undefined {
  if (!files?.length) return undefined;
  if (item.ticketId) {
    for (const file of files) {
      const version = file.versions?.find(
        (v) => v.reviewTicketId === item.ticketId,
      );
      if (version) return { file, version };
    }
  }

  const byReviewFile = matchVersionByReviewFiles(files, item.reviewFiles);
  if (byReviewFile) return byReviewFile;

  if (item.ticketId) {
    const byOrphan = matchOrphanInReviewVersion(files, item);
    if (byOrphan) return byOrphan;
  }

  if (item.fileId) {
    const file = files.find((f) => f.fileId === item.fileId);
    if (!file?.versions?.length) return undefined;
    const inReview = file.versions.filter(
      (v) => v.reviewStatus === "in_review",
    );
    if (inReview.length === 1) {
      return { file, version: inReview[0] };
    }
    if (item.versionNumber != null) {
      const byStoredNumber = file.versions.find(
        (v) => v.versionNumber === item.versionNumber,
      );
      if (byStoredNumber) return { file, version: byStoredNumber };
    }
    const latest = projectFileLatestVersion(file);
    if (latest && isProjectFileVersionListedInReviews(latest)) {
      return { file, version: latest };
    }
  }
  return undefined;
}

/** Map API review rows to display versions using each file's `versions` array order. */
export function enrichProjectReviewsFromFiles(
  reviews: ProjectReviewItem[],
  files: AskPunttProjectFile[] | undefined,
): ProjectReviewItem[] {
  const enriched = reviews.map((item) => {
    const resolved = findProjectFileVersionForReview(files, item);
    if (!resolved) return item;
    const ordinal = projectFileVersionOrdinal(resolved.file, resolved.version);
    const fileId = item.fileId ?? resolved.file.fileId;
    return {
      ...item,
      fileId,
      versionNumber: ordinal,
      id: fileId ? `${fileId}-v${ordinal}` : item.id,
    };
  });
  return [...enriched].sort(
    (a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0),
  );
}

/** One downloadable row in the Reference Documents list (per file version). */
export type ProjectReferenceDocumentRow = {
  file: AskPunttProjectFile;
  version: AskPunttProjectFile["versions"][number];
  /** 1-based index in `file.versions` (display badge). */
  versionOrdinal: number;
};

function versionUploadedAtMs(uploadedAt: string | Date | undefined): number {
  if (uploadedAt == null) return 0;
  if (uploadedAt instanceof Date) return uploadedAt.getTime();
  return Date.parse(uploadedAt) || 0;
}

function projectPdfIdentities(
  files: AskPunttProjectFile[] | undefined,
): Set<string> {
  const pdfIdentities = new Set<string>();
  for (const file of files ?? []) {
    for (const v of file.versions ?? []) {
      for (const src of [v.source, v.key]) {
        if (src && isPdfSource(src)) {
          pdfIdentities.add(referenceDocumentIdentity(src));
        }
      }
    }
  }
  return pdfIdentities;
}

function isReferenceVersionVisible(
  version: AskPunttProjectFile["versions"][number],
  pdfIdentities: Set<string>,
): boolean {
  const source = version.source ?? version.key;
  if (!source) return true;
  if (!isOfficeSource(source)) return true;
  return !pdfIdentities.has(referenceDocumentIdentity(source));
}

/**
 * Every project-file version as a Reference Documents row (newest first).
 * Includes versions that also appear in Reviews — the panel is the full
 * version history, not “what’s left after Reviews.” Office originals are still
 * hidden when the same upload exists as a PDF on the project.
 */
export function projectReferenceDocumentRows(
  files: AskPunttProjectFile[] | undefined,
): ProjectReferenceDocumentRow[] {
  const pdfIdentities = projectPdfIdentities(files);
  const rows: ProjectReferenceDocumentRow[] = [];
  for (const file of files ?? []) {
    const versions = file.versions ?? [];
    versions.forEach((version, index) => {
      if (!isReferenceVersionVisible(version, pdfIdentities)) return;
      rows.push({
        file,
        version,
        versionOrdinal: index + 1,
      });
    });
  }
  return rows.sort((a, b) => {
    const ta = versionUploadedAtMs(a.version.uploadedAt);
    const tb = versionUploadedAtMs(b.version.uploadedAt);
    if (tb !== ta) return tb - ta;
    if (a.file.fileId !== b.file.fileId) {
      return a.file.fileId.localeCompare(b.file.fileId);
    }
    return b.versionOrdinal - a.versionOrdinal;
  });
}

/** @deprecated Use `projectReferenceDocumentRows` — one row per version. */
export function projectReferenceFiles(
  files: AskPunttProjectFile[] | undefined,
): AskPunttProjectFile[] {
  const seen = new Set<string>();
  const out: AskPunttProjectFile[] = [];
  for (const row of projectReferenceDocumentRows(files)) {
    if (seen.has(row.file.fileId)) continue;
    seen.add(row.file.fileId);
    out.push(row.file);
  }
  return out;
}
